import { $isListNode, ListNode } from '@lexical/list';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $isHeadingNode } from '@lexical/rich-text';
import {
  $findMatchingParent,
  $getNearestNodeOfType,
  mergeRegister,
} from '@lexical/utils';
import { Box } from '@mui/material';
import {
  $getSelection,
  $isRangeSelection,
  $isRootOrShadowRoot,
  $isTextNode,
  FORMAT_TEXT_COMMAND,
  SELECTION_CHANGE_COMMAND,
} from 'lexical';
import { useCallback, useEffect, useState } from 'react';

import { Button } from '@/components/form/baseInputs/Button';
import { Bold01Icon } from '@/components/icons/Bold01Icon';
import { Strikethrough01Icon } from '@/components/icons/Strikethrough01Icon';
import { Trash01Icon } from '@/components/icons/Trash01Icon';
import { Underline01Icon } from '@/components/icons/Underline01Icon';
import { ConfirmationModal } from '@/components/modals/ConfirmationModal/ConfirmationModal';
import { $isFontSizeNode } from '@/components/TextEditor/plugins/FontSizePlugin/FontSizeNode';
import { Block } from '@/components/TextEditor/plugins/ToolbarPlugin/ToolbarPlugin.utils';
import { SlideEditorBlockDropdown } from '@/modules/slideEditor/textEditors/SlideEditor.blockDropdown';
import { SlideEditorFontSizeDropdown } from '@/modules/slideEditor/textEditors/SlideEditor.bodyToolbar';
import { SlideEditorHighlightButton } from '@/modules/slideEditor/textEditors/SlideEditor.highlightButton';
import {
  SlideEditorToolbar,
  useSlideEditorToolbarContext,
} from '@/modules/slideEditor/textEditors/SlideEditor.toolbar';
import { SlideEditorToolbarDivider } from '@/modules/slideEditor/textEditors/SlideEditor.toolbarDivider';
import { useToolbarDropdown } from '@/modules/slideEditor/textEditors/SlideEditor.toolbarDropdown';
import { COLORS } from '@/styles/tokens/colors';
import {
  TEXT_NOTE_DEFAULT_FONT_SIZE,
  TEXT_NOTE_FONT_SIZES,
  type TextNoteFontSize,
} from '@/styles/tokens/fonts';

const LowPriority = 1;

/** Background applied when a format (bold/underline/strikethrough) is active.
 *  Matches the transparent-variant hover background, so the toggled-on state
 *  is indistinguishable from hover. Mirrors `ACTIVE_FORMAT_BUTTON_SX` in
 *  `SlideEditor.bodyToolbar`. */
const ACTIVE_FORMAT_BUTTON_SX = {
  '&&': { bgcolor: COLORS.GRAY[100] },
};

interface TextNoteToolbarPluginProps {
  onDelete: () => void;
  /**
   * When true, Delete asks the user to confirm first. Set it to false when
   * the user can undo the delete.
   */
  confirmDelete?: boolean;
}

/**
 * Floating toolbar for text note nodes. Matches the visual chrome and button
 * styling used across slide editor toolbars (see `SlideEditorToolbar`), but
 * with the note-specific button set: Body dropdown, Bold, Strikethrough, and
 * a destructive Delete action that confirms before firing.
 */
export function TextNoteToolbarPlugin({
  onDelete,
  confirmDelete = true,
}: TextNoteToolbarPluginProps) {
  return (
    // Capture-phase preventDefault keeps the editor focused when the user
    // clicks any toolbar control, so commands dispatch against the live
    // selection instead of losing it.
    <Box onMouseDownCapture={(e) => e.preventDefault()}>
      <SlideEditorToolbar>
        <TextNoteToolbarButtons
          onDelete={onDelete}
          confirmDelete={confirmDelete}
        />
      </SlideEditorToolbar>
    </Box>
  );
}

type TextNoteToolbarButtonsProps = Required<TextNoteToolbarPluginProps>;

function TextNoteToolbarButtons({
  onDelete,
  confirmDelete,
}: TextNoteToolbarButtonsProps) {
  const { activeDropdown, setActiveDropdown } = useSlideEditorToolbarContext();
  const [editor] = useLexicalComposerContext();
  const [blockType, setBlockType] = useState<Block>('paragraph');
  const [isBold, setIsBold] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [isStrikethrough, setIsStrikethrough] = useState(false);
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [currentFontSize, setCurrentFontSize] = useState<
    TextNoteFontSize | 'mixed' | null
  >(null);

  const blockDropdown = useToolbarDropdown('block');
  const fontSizeDropdown = useToolbarDropdown('fontSize');

  const updateToolbar = useCallback(() => {
    const selection = $getSelection();
    if (!$isRangeSelection(selection)) return;

    const anchorNode = selection.anchor.getNode();
    let element =
      anchorNode.getKey() === 'root'
        ? anchorNode
        : $findMatchingParent(anchorNode, (e) => {
            const parent = e.getParent();
            return parent !== null && $isRootOrShadowRoot(parent);
          });

    if (element === null) {
      element = anchorNode.getTopLevelElementOrThrow();
    }

    const elementKey = element.getKey();
    if (editor.getElementByKey(elementKey) !== null) {
      if ($isListNode(element)) {
        const parentList = $getNearestNodeOfType(anchorNode, ListNode);
        const type = parentList ? parentList.getTag() : element.getTag();
        setBlockType(type);
      } else {
        const type = $isHeadingNode(element)
          ? (element.getTag() as Block)
          : (element.getType() as Block);
        setBlockType(type);
      }
    }

    setIsBold(selection.hasFormat('bold'));
    setIsUnderline(selection.hasFormat('underline'));
    setIsStrikethrough(selection.hasFormat('strikethrough'));

    // Detect font size across the selection
    const selectedNodes = selection.getNodes();
    const sizes = new Set<number | null>();
    for (const node of selectedNodes) {
      if (!$isTextNode(node) && !$isFontSizeNode(node)) continue;
      const fsNode = $isFontSizeNode(node)
        ? node
        : $findMatchingParent(node, $isFontSizeNode);
      sizes.add(fsNode ? fsNode.getFontSize() : TEXT_NOTE_DEFAULT_FONT_SIZE);
    }
    if (sizes.size > 1) {
      setCurrentFontSize('mixed');
    } else {
      const onlySize = sizes.values().next().value;
      setCurrentFontSize((onlySize as TextNoteFontSize) ?? null);
    }
  }, [editor]);

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => updateToolbar());
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          updateToolbar();
          return false;
        },
        LowPriority
      )
    );
  }, [editor, updateToolbar]);

  return (
    <>
      <div ref={blockDropdown.containerRef}>
        <SlideEditorBlockDropdown
          editor={editor}
          blockType={blockType}
          isOpen={activeDropdown === 'block'}
          onClick={() =>
            setActiveDropdown((prev) => (prev === 'block' ? null : 'block'))
          }
          onClose={() =>
            setActiveDropdown((prev) => (prev === 'block' ? null : prev))
          }
        />
      </div>
      <SlideEditorFontSizeDropdown
        containerRef={fontSizeDropdown.containerRef}
        currentFontSize={currentFontSize}
        blockType={blockType}
        isOpen={activeDropdown === 'fontSize'}
        onToggle={() =>
          setActiveDropdown((prev) => (prev === 'fontSize' ? null : 'fontSize'))
        }
        onClose={() =>
          setActiveDropdown((prev) => (prev === 'fontSize' ? null : prev))
        }
        allowedFontSizes={TEXT_NOTE_FONT_SIZES}
        defaultFontSize={TEXT_NOTE_DEFAULT_FONT_SIZE}
      />
      {/* Italic is intentionally omitted — it doesn't round-trip through
          markdown serialization in this editor configuration. */}
      <Button
        size="xs"
        variant="transparent"
        square
        onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold')}
        sx={isBold ? ACTIVE_FORMAT_BUTTON_SX : undefined}
      >
        <Bold01Icon size={18} />
      </Button>
      <Button
        size="xs"
        variant="transparent"
        square
        onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline')}
        sx={isUnderline ? ACTIVE_FORMAT_BUTTON_SX : undefined}
      >
        <Underline01Icon size={18} />
      </Button>
      <Button
        size="xs"
        variant="transparent"
        square
        onClick={() =>
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'strikethrough')
        }
        sx={isStrikethrough ? ACTIVE_FORMAT_BUTTON_SX : undefined}
      >
        <Strikethrough01Icon size={18} />
      </Button>
      <SlideEditorHighlightButton />
      <SlideEditorToolbarDivider />
      <Button
        size="xs"
        variant="destructive-transparent"
        startIcon={Trash01Icon}
        onClick={() =>
          confirmDelete ? setIsConfirmingDelete(true) : onDelete()
        }
      >
        Delete
      </Button>
      {isConfirmingDelete && (
        <ConfirmationModal
          isOpen
          heading="Delete note"
          confirmText="Delete"
          cancelText="Cancel"
          closeOnOutsideClick={!isDeleting}
          confirmButtonProps={{
            variant: 'destructive-primary',
            loading: isDeleting,
          }}
          cancelButtonProps={{ disabled: isDeleting }}
          onClose={(reason) => {
            if (reason === 'cancel') {
              setIsConfirmingDelete(false);
              return;
            }
            // Defer the dispatch so React commits the loading state before
            // the delete cascade (history snapshot clone + every-tile re-render)
            // blocks the main thread. Without this yield, the spinner never
            // gets a chance to paint.
            setIsDeleting(true);
            setTimeout(() => onDelete(), 0);
          }}
        >
          Are you sure you&apos;d like to delete this note? This cannot be
          undone.
        </ConfirmationModal>
      )}
    </>
  );
}
